
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { TranscriptionStatus, TranscriptionJob, TranscriptionHistoryItem } from '../types';
import { transcribeMedia } from '../services/geminiService';
import { transcribeMedia as transcribeMediaLocal } from '../services/localTranscriptionService';
import { useTranscriptionMode } from '../contexts/TranscriptionModeContext';
import TranscriptionModeToggle from './TranscriptionModeToggle';
import { addToHistory, updateHistoryItem } from '../services/historyService';
import { compressAudio, preloadFFmpeg } from '../services/ffmpegService';
import { setSessionJobs, getSessionJobs, clearSessionJobs } from '../services/jobService';
import { getUserCredits, getUserProfile, deductUserCredits, subscribeToUserProfile } from '../services/userService';
import { getMediaDuration, formatTime } from '../services/mediaUtils';
import { auth, logoutUser, onAuthStateChanged } from '../services/supabase';
import type { User } from '../services/supabase';
import FileUploader from './FileUploader';
import HistoryPage from './HistoryPage';
import JobItem from './JobItem';
import LoginModal from './LoginModal';
import TranscriptPage from './TranscriptPage';
import { LogIn, LogOut, MicVocal, History, UploadCloud, Sparkles, Zap, Shield, Bot, FileAudio, LayoutDashboard, Timer, Lock, CheckCircle2, AlertCircle, X, StopCircle } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import TranscribingText from './TranscribingText';
import AdGateModal from './AdGateModal';
import { SkeletonCreditBar, DashboardSkeleton } from './Skeleton';
// @ts-ignore
import confetti from 'canvas-confetti';

const MAX_CONCURRENT_JOBS = 3;

type ViewableItem = {
    id?: string;
    fileName: string;
    transcript?: string;
    file?: File;
    mediaData?: string;
    mediaType?: string;
};

type Toast = { id: string; message: string; type: 'info' | 'success' | 'error' };

const FeatureCard: React.FC<{ icon: React.ReactNode; title: string; description: string }> = ({ icon, title, description }) => (
  <div className="group relative bg-white/50 dark:bg-dark-200/50 backdrop-blur-sm p-6 rounded-2xl border border-base-200 dark:border-dark-300 hover:border-brand-primary/30 dark:hover:border-brand-primary/30 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    <div className="relative z-10">
        <div className="w-12 h-12 flex items-center justify-center bg-brand-primary/10 rounded-xl text-brand-primary mb-4 group-hover:scale-110 transition-transform duration-300">
        {icon}
        </div>
        <h3 className="text-lg font-bold text-base-content dark:text-dark-content mb-2">{title}</h3>
        <p className="text-base-content/70 dark:text-dark-content/70 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const ToastItem: React.FC<{ toast: Toast; onDismiss: (id: string) => void }> = ({ toast, onDismiss }) => {
  const iconMap = {
    success: <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />,
    error: <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />,
    info: <FileAudio className="w-4 h-4 text-brand-primary flex-shrink-0" />,
  };
  const bgMap = {
    success: 'border-green-500/20 bg-green-50 dark:bg-green-900/20',
    error: 'border-red-500/20 bg-red-50 dark:bg-red-900/20',
    info: 'border-brand-primary/20 bg-white dark:bg-dark-200',
  };
  return (
    <div style={{ animation: 'toastIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards' }} className={`flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg shadow-black/10 dark:shadow-black/30 min-w-[220px] max-w-[320px] ${bgMap[toast.type]}`}>
      {iconMap[toast.type]}
      <p className="text-sm font-medium text-base-content dark:text-dark-content flex-1 truncate">{toast.message}</p>
      <button onClick={() => onDismiss(toast.id)} className="text-base-content/40 hover:text-base-content dark:text-dark-content/40 dark:hover:text-dark-content transition-colors flex-shrink-0">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const DashboardPage: React.FC = () => {
  const { isLocal } = useTranscriptionMode();

  const [jobs, setJobs] = useState<TranscriptionJob[]>([]);
  const [page, setPage] = useState<'dashboard' | 'history' | 'transcript'>('dashboard');
  const [previousPage, setPreviousPage] = useState<'dashboard' | 'history'>('dashboard');
  const [scrolled, setScrolled] = useState(false);

  const [user, setUser] = useState<User | null>(null);
  const [credits, setCredits] = useState<number | null>(null);
  const [isPaid, setIsPaid] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [currentViewItem, setCurrentViewItem] = useState<ViewableItem | null>(null);
  const [adGateItem, setAdGateItem] = useState<ViewableItem | null>(null);
  const adShownJobs = useRef<Set<string>>(new Set());

  // Cancel job
  const cancelledJobs = useRef<Set<string>>(new Set());
  const [cancelConfirmJobId, setCancelConfirmJobId] = useState<string | null>(null);

  // Toast notifications
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = useCallback((message: string, type: Toast['type'] = 'info') => {
    const id = crypto.randomUUID();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);
  const dismissToast = useCallback((id: string) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  // Progress timers
  const progressTimers = useRef<Record<string, ReturnType<typeof setInterval>>>({});

  useEffect(() => {
    preloadFFmpeg();
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadCredits = useCallback(async () => {
    const profile = await getUserProfile();
    if (profile) {
      setCredits(profile.credits);
      setIsPaid(profile.isPaid);
    } else {
      const c = await getUserCredits();
      setCredits(c);
    }
    setProfileLoaded(true);
  }, []);

  useEffect(() => {
    let profileUnsub: (() => void) | null = null;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
        setUser(currentUser);
        setProfileLoaded(false);
        loadCredits();
        if (currentUser) {
            profileUnsub = subscribeToUserProfile(currentUser.id, (profile) => {
                if (typeof profile.isPaid === 'boolean') setIsPaid(profile.isPaid);
                if (typeof profile.credits === 'number') setCredits(profile.credits);
            });
        } else {
            setIsPaid(false);
            if (profileUnsub) { profileUnsub(); profileUnsub = null; }
        }
    });
    return () => { unsubscribe(); if (profileUnsub) profileUnsub(); };
  }, [loadCredits]);

  // Simulate progress for PROCESSING jobs
  useEffect(() => {
    jobs.forEach(job => {
      if (job.status === TranscriptionStatus.PROCESSING && !progressTimers.current[job.id]) {
        progressTimers.current[job.id] = setInterval(() => {
          setJobs(prev => prev.map(j =>
            j.id === job.id && j.status === TranscriptionStatus.PROCESSING
              ? { ...j, progress: Math.min((j.progress ?? 0) + (isLocal ? 1 : 4), 95) }
              : j
          ));
        }, isLocal ? 2500 : 700);
      }
      if ((job.status === TranscriptionStatus.SUCCESS || job.status === TranscriptionStatus.ERROR) && progressTimers.current[job.id]) {
        clearInterval(progressTimers.current[job.id]);
        delete progressTimers.current[job.id];
      }
    });
    return () => {
      const activeIds = new Set(jobs.filter(j => j.status === TranscriptionStatus.PROCESSING).map(j => j.id));
      Object.keys(progressTimers.current).forEach(id => {
        if (!activeIds.has(id)) {
          clearInterval(progressTimers.current[id]);
          delete progressTimers.current[id];
        }
      });
    };
  }, [jobs, isLocal]);

  const processQueue = useCallback(async () => {
    const processingJobs = jobs.filter(j => j.status === TranscriptionStatus.PROCESSING).length;
    const queuedJobs = jobs.filter(j => j.status === TranscriptionStatus.UPLOADING);

    if (processingJobs < MAX_CONCURRENT_JOBS && queuedJobs.length > 0) {
      const jobToProcess = queuedJobs[0];
      const id = jobToProcess.id;

      setJobs(prevJobs => prevJobs.map(j =>
        j.id === id ? { ...j, status: TranscriptionStatus.PROCESSING, startTime: Date.now(), progress: 0 } : j
      ));
      showToast(`Transcribing: ${jobToProcess.fileName}`, 'info');
      console.log(`[EchoScribe] Transcribing: ${jobToProcess.fileName}`);

      try {
        let transcript: string;
        let originalTranscript: string | undefined;
        let mediaData: string | undefined;

        if (isLocal) {
          // Start compression in background — runs while Whisper transcribes (both read the same immutable File)
          const compressionPromise = compressAudio(jobToProcess.file).catch((e) => {
            console.error('[EchoScribe] FFmpeg compression failed, audio will not be saved', e);
            return null;
          });

          console.log(`[EchoScribe] Local mode: transcribing + compressing in parallel for "${jobToProcess.fileName}"`);

          if (cancelledJobs.current.has(id)) return;

          const result = await transcribeMediaLocal(
            jobToProcess.file,
            (pct) => {
              setJobs(prev => prev.map(j => j.id === id ? { ...j, progress: pct } : j));
              console.log(`[EchoScribe] ${jobToProcess.fileName} — ${pct}%`);
            },
            (chunkText) => {
              setJobs(prev => prev.map(j =>
                j.id === id
                  ? { ...j, partialTranscript: (j.partialTranscript ? j.partialTranscript + '\n' : '') + chunkText }
                  : j
              ));
            }
          );
          transcript = result.transcript;
          originalTranscript = result.originalTranscript;

          // Compression almost certainly finished already — await and store for DB
          const compressed = await compressionPromise;
          if (compressed) mediaData = compressed.base64;

        } else {
          // Cloud: compress first so Gemini receives a smaller file (faster upload + inference)
          let fileForTranscription = jobToProcess.file;
          try {
            console.log(`[EchoScribe] Cloud mode: compressing "${jobToProcess.fileName}" before upload to Gemini`);
            const compressed = await compressAudio(jobToProcess.file);
            fileForTranscription = compressed.file;
            mediaData = compressed.base64;
          } catch (e) {
            console.error('[EchoScribe] FFmpeg compression failed, sending original to Gemini', e);
          }

          if (cancelledJobs.current.has(id)) return;

          transcript = await transcribeMedia(fileForTranscription);
        }

        if (cancelledJobs.current.has(id)) return;

        const finalDuration = jobToProcess.mediaDuration || (Date.now() - (jobToProcess.startTime ?? Date.now()));
        if (!isPaid) {
          await deductUserCredits(finalDuration);
          setCredits(prev => (prev !== null ? Math.max(0, prev - finalDuration) : 0));
        }
        await addToHistory({ id, fileName: jobToProcess.fileName, transcript, timestamp: Date.now(), duration: finalDuration, mediaData, mediaType: mediaData ? 'audio/mpeg' : jobToProcess.file?.type, originalTranscript });

        setJobs(prevJobs => prevJobs.map(j =>
          j.id === id ? { ...j, status: TranscriptionStatus.SUCCESS, transcript, originalTranscript, progress: 100 } : j
        ));
        showToast(`Done: ${jobToProcess.fileName}`, 'success');

        const completedItem = { id, fileName: jobToProcess.fileName, transcript, originalTranscript, file: jobToProcess.file };
        setTimeout(() => {
            setPreviousPage('dashboard');
            setCurrentViewItem(completedItem);
            if (isPaid) {
              setPage('transcript');
            } else {
              setAdGateItem(completedItem);
            }
        }, 800);

      } catch (error) {
        if (cancelledJobs.current.has(id)) return;
        console.error('Transcription failed:', error);
        showToast(`Failed: ${jobToProcess.fileName}`, 'error');
        setJobs(prevJobs => prevJobs.map(j =>
          j.id === id ? { ...j, status: TranscriptionStatus.ERROR, error: error instanceof Error ? error.message : String(error) } : j
        ));
      }
    }
  }, [jobs, isLocal, showToast]);

  useEffect(() => {
    processQueue();
  }, [jobs, processQueue]);

  useEffect(() => {
    const storedJobs = getSessionJobs();
    if (storedJobs.length > 0) {
        const restoredJobs: TranscriptionJob[] = storedJobs.map(sj => ({
            ...sj,
            file: new File([], sj.fileName),
        }));
        setJobs(restoredJobs);
    }
  }, []);

  useEffect(() => {
    setSessionJobs(jobs);
  }, [jobs]);

  const handleFileSelect = async (file: File) => {
    if (!isPaid && credits !== null && credits <= 0) {
        alert("You have run out of free transcription minutes. Please contact support or upgrade.");
        return;
    }
    showToast(`Received: ${file.name}`, 'info');
    const duration = await getMediaDuration(file);
    const newJob: TranscriptionJob = {
      id: crypto.randomUUID(),
      file,
      fileName: file.name,
      status: TranscriptionStatus.UPLOADING,
      mediaDuration: duration
    };
    setJobs(prevJobs => [...prevJobs, newJob]);
  };

  const handleRetry = (jobToRetry: TranscriptionJob) => {
    setJobs(prevJobs => prevJobs.map(j =>
      j.id === jobToRetry.id ? { ...j, status: TranscriptionStatus.UPLOADING, error: undefined } : j
    ));
  };

  const handleCancel = (jobId: string) => {
    setCancelConfirmJobId(jobId);
  };

  const confirmCancel = () => {
    if (!cancelConfirmJobId) return;
    cancelledJobs.current.add(cancelConfirmJobId);
    setJobs(prev => prev.map(j =>
      j.id === cancelConfirmJobId ? { ...j, status: TranscriptionStatus.ERROR, error: 'Cancelled' } : j
    ));
    showToast('Job cancelled', 'error');
    setCancelConfirmJobId(null);
  };

  const handleAdContinue = () => {
    if (adGateItem?.id) adShownJobs.current.add(adGateItem.id);
    setAdGateItem(null);
    setPage('transcript');
  };

  const handleViewRequest = (item: ViewableItem) => {
    setCurrentViewItem(item);
    if (!user) {
        setShowLoginModal(true);
    } else {
        if (page !== 'transcript') {
            setPreviousPage(page);
        }
        if (isPaid || (item.id && adShownJobs.current.has(item.id))) {
            setPage('transcript');
        } else {
            setAdGateItem(item);
        }
    }
  };

  const handleLoginSuccess = () => {
    setShowLoginModal(false);
    loadCredits();
    if (currentViewItem) {
        if (page !== 'transcript') {
            setPreviousPage(page);
        }
        if (isPaid || (currentViewItem.id && adShownJobs.current.has(currentViewItem.id))) {
            setPage('transcript');
        } else {
            setAdGateItem(currentViewItem);
        }
    }
  };

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const confirmLogout = async () => {
    await logoutUser();
    clearSessionJobs();
    setUser(null);
    setJobs([]);
    loadCredits();
    setPage('dashboard');
    setShowLogoutConfirm(false);
  };

  const navigateTo = (targetPage: 'dashboard' | 'history') => {
      setPage(targetPage);
  };

  const handleBack = () => {
    setPage(previousPage);
    setCurrentViewItem(null);
  };

  const handleLogoClick = () => {
    if (page !== 'dashboard') {
      navigateTo('dashboard');
    }
  };

  const handleSaveTranscript = (newTranscript: string) => {
    if (!currentViewItem) return;
    setJobs(prevJobs => prevJobs.map(j =>
        j.fileName === currentViewItem.fileName ? { ...j, transcript: newTranscript } : j
    ));
    setCurrentViewItem(prev => prev ? { ...prev, transcript: newTranscript } : null);
    updateHistoryItem(currentViewItem.id || currentViewItem.fileName, newTranscript);
  };

  const isUploadDisabled = !isPaid && credits !== null && credits <= 0;

  const renderCreditBar = () => {
    if (credits === null) return <SkeletonCreditBar />;
    const totalMinutes = 10;
    const totalMs = totalMinutes * 60 * 1000;
    const percentage = Math.max(0, Math.min(100, (credits / totalMs) * 100));

    return (
        <div className="w-full max-w-3xl mx-auto px-4 mb-4 animate-fade-in">
             <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-base-content/60 dark:text-dark-content/60 mb-2">
                <span className="flex items-center gap-1"><Timer size={12}/> Free Plan Credits</span>
                <span>{Math.ceil(credits / 1000 / 60)} min remaining</span>
             </div>
             <div className="w-full h-2 bg-base-200 dark:bg-dark-300 rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-700 ${credits < 60000 ? 'bg-red-500' : 'bg-brand-primary'}`}
                    style={{ width: `${percentage}%` }}
                />
             </div>
             <div className="flex items-center justify-between mt-2">
               {credits <= 0 ? (
                 <p className="text-red-500 text-sm font-medium flex items-center gap-2">
                   <Lock size={14} /> Limit reached.
                 </p>
               ) : <span />}
               <a
                 href="/pricing"
                 className={`text-xs font-semibold px-3 py-1 rounded-full transition-all ${
                   credits <= 0
                     ? 'bg-brand-primary text-white shadow-md scale-105'
                     : percentage < 50
                     ? 'bg-brand-primary/20 text-brand-primary hover:bg-brand-primary hover:text-white'
                     : 'text-base-content/50 dark:text-dark-content/50 hover:text-brand-primary'
                 }`}
               >
                 Upgrade
               </a>
             </div>
        </div>
    );
  };

  const renderPage = () => {
    switch (page) {
      case 'history':
        return <HistoryPage onNavigateToDashboard={() => navigateTo('dashboard')} onView={(item) => handleViewRequest(item)} isPaid={isPaid} />;
      case 'transcript':
        return currentViewItem
          ? <TranscriptPage transcriptData={currentViewItem} onBack={handleBack} onSave={handleSaveTranscript} isPaid={isPaid} />
          : <div className="text-center text-base-content dark:text-dark-content">No transcript selected.</div>;
      case 'dashboard':
      default:
        if (!profileLoaded) return <DashboardSkeleton />;
        return (
          <div className="w-full flex flex-col items-center gap-8 pb-12" style={{ animation: 'fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}>

            {/* Hero */}
            <section className="relative text-center space-y-4 pt-2 px-4 animate-fade-in max-w-4xl mx-auto z-10 min-h-[360px] md:min-h-[400px] flex flex-col justify-center items-center">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] md:w-[600px] md:h-[600px] bg-gradient-to-tr from-brand-primary/20 via-brand-secondary/20 to-blue-500/20 rounded-full blur-[100px] -z-10 animate-pulse-subtle pointer-events-none" />
                <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-base-200/50 dark:bg-dark-200/50 border border-base-300 dark:border-dark-300 backdrop-blur-md shadow-sm">
                    <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
                    <span className="text-xs font-semibold text-base-content/80 dark:text-dark-content/80">Powered by inhouse LLM models</span>
                </div>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-base-content dark:text-dark-content tracking-tight leading-tight">
                    Transcribe media <br/>
                    <TranscribingText />
                </h1>
                <p className="text-base md:text-lg text-base-content/60 dark:text-dark-content/60 max-w-2xl mx-auto leading-relaxed font-light">
                    Transform audio and video into accurate text instantly. <br className="hidden md:block" /> Secure, fast, and multimodal.
                </p>
            </section>

            {!isPaid && renderCreditBar()}

            {/* Uploader */}
            <section className="w-full max-w-3xl px-4 animate-fade-in-up z-10">
                 <div className="bg-white/40 dark:bg-dark-200/40 backdrop-blur-xl p-2 rounded-3xl shadow-2xl border border-white/20 dark:border-dark-300/50 relative">
                    <FileUploader
                        onFileSelect={handleFileSelect}
                        disabled={isUploadDisabled || jobs.filter(j => j.status === TranscriptionStatus.PROCESSING || j.status === TranscriptionStatus.UPLOADING).length >= MAX_CONCURRENT_JOBS * 2}
                        isPaid={isPaid}
                    />
                 </div>
            </section>

            {/* Queue */}
            {jobs.length > 0 && (
              <section className="w-full max-w-2xl space-y-4 px-4 animate-fade-in">
                  <div className="flex items-center justify-between">
                    <h2 className="text-sm font-bold text-base-content dark:text-dark-content flex items-center gap-2 uppercase tracking-wider opacity-60">
                        <LayoutDashboard className="w-4 h-4 text-brand-primary" />
                        Queue
                    </h2>
                    <span className="text-[10px] px-2 py-0.5 rounded-md bg-base-200 dark:bg-dark-300 text-base-content/60 dark:text-dark-content/60 font-mono">
                        {jobs.filter(j => j.status !== TranscriptionStatus.SUCCESS && j.status !== TranscriptionStatus.ERROR).length} Active
                    </span>
                  </div>
                  <div className="space-y-3">
                      {jobs.map((job, i) => (
                          <div key={job.id} style={{ opacity: 0, animation: `slideInRight 0.35s cubic-bezier(0.16,1,0.3,1) ${i * 40}ms forwards` }}>
                            <JobItem job={job} onRetry={handleRetry} onView={() => handleViewRequest(job)} onCancel={handleCancel} />
                          </div>
                      ))}
                  </div>
              </section>
            )}

            {/* Features */}
            <section className="w-full max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 px-4 mt-4 animate-fade-in">
                <FeatureCard icon={<Bot size={24} />} title="Inhouse AI" description="Built on proprietary LLM models for superior multimodal understanding." />
                <FeatureCard icon={<FileAudio size={24} />} title="Any Format" description="Drag and drop audio or video. If you can play it, we can type it." />
                <FeatureCard icon={<Zap size={24} />} title="Instant Results" description="Lightning fast processing speeds using advanced neural architecture." />
                <FeatureCard icon={<Shield size={24} />} title="Private & Secure" description="Your data is processed securely and history is stored locally." />
            </section>

            {/* How it works */}
            <section className="w-full max-w-5xl px-4 animate-fade-in">
                <div className="bg-gradient-to-br from-base-200 to-base-100 dark:from-dark-200 dark:to-dark-100 rounded-3xl p-6 md:p-12 border border-base-200 dark:border-dark-300 shadow-sm relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-brand-secondary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                    <div className="relative z-10">
                        <h2 className="text-xl md:text-2xl font-bold text-base-content dark:text-dark-content mb-6 flex items-center gap-3">
                            <Bot className="text-brand-primary" />
                            How it works
                        </h2>
                        <div className="prose dark:prose-invert max-w-none text-base-content/70 dark:text-dark-content/70 space-y-4">
                            <p>EchoScribe leverages <strong>proprietary neural processing</strong> to convert speech directly into structured text. Unlike traditional transcription services that convert audio to waveforms, we process the media file directly using our <strong>inhouse LLM models</strong>.</p>
                            <p>This allows for superior context awareness, speaker differentiation, and the ability to understand complex vocabulary and nuances that standard speech-to-text engines often miss.</p>
                        </div>
                    </div>
                </div>
            </section>

            <footer className="text-center text-sm text-base-content/40 dark:text-dark-content/40 py-8">
                <p>© {new Date().getFullYear()} EchoScribe. Powered by inhouse LLM models.</p>
            </footer>

          </div>
        );
    }
  };

  return (
    <div className="flex flex-col items-center justify-start w-full min-h-screen bg-base-100 dark:bg-dark-100 transition-colors duration-300">
        {/* Header */}
        <header className={`fixed top-0 w-full z-50 transition-all duration-300 border-b ${scrolled ? 'bg-base-100/80 dark:bg-dark-100/80 backdrop-blur-md border-base-200 dark:border-dark-300 shadow-sm py-2' : 'bg-transparent border-transparent py-4'}`}>
            <div className="max-w-6xl mx-auto px-4 md:px-8 flex justify-between items-center">
                <div onClick={handleLogoClick} className="flex items-center gap-2.5 cursor-pointer group" role="button" tabIndex={0}>
                    <div className="w-9 h-9 bg-brand-primary rounded-xl flex items-center justify-center text-white shadow-lg shadow-brand-primary/20 group-hover:scale-105 transition-transform duration-300">
                        <MicVocal className="w-5 h-5" />
                    </div>
                    <h1 className="text-lg font-bold text-base-content dark:text-dark-content tracking-tight group-hover:text-brand-primary transition-colors">EchoScribe</h1>
                </div>

                <nav className="flex items-center gap-1 sm:gap-2">
                    {user && (
                        <>
                            <button onClick={() => navigateTo('dashboard')} className={`p-2 rounded-xl transition-all duration-200 ${page === 'dashboard' ? 'bg-base-200 dark:bg-dark-300 text-brand-primary' : 'text-base-content/60 dark:text-dark-content/60 hover:bg-base-200 dark:hover:bg-dark-300'}`} title="Dashboard">
                                <UploadCloud size={18} />
                            </button>
                            <button onClick={() => navigateTo('history')} className={`p-2 rounded-xl transition-all duration-200 ${page === 'history' ? 'bg-base-200 dark:bg-dark-300 text-brand-primary' : 'text-base-content/60 dark:text-dark-content/60 hover:bg-base-200 dark:hover:bg-dark-300'}`} title="History">
                                <History size={18} />
                            </button>
                            <div className="w-px h-5 bg-base-300 dark:bg-dark-300 mx-1" />
                        </>
                    )}
                    <TranscriptionModeToggle />
                    <ThemeToggle />
                    {user ? (
                        <div className="flex items-center gap-2">
                            {user.user_metadata?.avatar_url ? (
                                <img src={user.user_metadata.avatar_url} alt={user.user_metadata?.full_name || user.user_metadata?.name || "User"} className="w-8 h-8 rounded-full border border-base-300 dark:border-dark-300" />
                            ) : (
                                <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-bold">
                                    {user.email?.[0].toUpperCase()}
                                </div>
                            )}
                            <button onClick={handleLogoutClick} className="p-2 rounded-xl text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors">
                                <LogOut size={16} />
                            </button>
                        </div>
                    ) : (
                        <button onClick={() => setShowLoginModal(true)} className="flex items-center gap-1.5 bg-brand-primary text-white text-xs font-bold px-3 py-2.5 rounded-xl hover:bg-brand-dark hover:shadow-lg hover:shadow-brand-primary/20 transition-all active:scale-95">
                            <LogIn size={16} />
                            <span>Login</span>
                        </button>
                    )}
                </nav>
            </div>
        </header>

        {/* Main — fade on page change */}
        <main className="w-full pt-16 md:pt-20 flex justify-center min-h-screen relative overflow-hidden">
            <div key={page} style={{ animation: 'fadeIn 0.35s cubic-bezier(0.16,1,0.3,1) forwards' }} className="w-full flex justify-center">
                {renderPage()}
            </div>
        </main>

        {/* Toast stack */}
        {toasts.length > 0 && (
          <div className="fixed bottom-6 right-4 sm:right-6 z-[96] flex flex-col gap-2 items-end pointer-events-none">
            {toasts.map(toast => (
              <div key={toast.id} className="pointer-events-auto">
                <ToastItem toast={toast} onDismiss={dismissToast} />
              </div>
            ))}
          </div>
        )}

        {showLoginModal && (
            <LoginModal onLoginSuccess={handleLoginSuccess} onClose={() => setShowLoginModal(false)} />
        )}

        {adGateItem && (
            <AdGateModal onContinue={handleAdContinue} />
        )}

        {/* Cancel confirm */}
        {cancelConfirmJobId && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fade-in" onClick={() => setCancelConfirmJobId(null)}>
                <div style={{ animation: 'scaleIn 0.25s cubic-bezier(0.16,1,0.3,1) forwards' }} className="bg-base-100 dark:bg-dark-200 p-6 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 max-w-sm w-full" onClick={e => e.stopPropagation()}>
                    <div className="flex flex-col items-center text-center mb-6">
                        <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center text-orange-500 mb-4">
                            <StopCircle size={24} />
                        </div>
                        <h3 className="text-xl font-bold text-base-content dark:text-dark-content mb-2">Cancel Job?</h3>
                        <p className="text-base-content/60 dark:text-dark-content/60 text-sm">The transcription is still in progress. Cancelling will discard any results.</p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setCancelConfirmJobId(null)} className="flex-1 px-4 py-2.5 rounded-xl border border-base-300 dark:border-dark-300 font-semibold text-base-content dark:text-dark-content hover:bg-base-200 dark:hover:bg-dark-300 transition-colors">Keep Going</button>
                        <button onClick={confirmCancel} className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20">Cancel Job</button>
                    </div>
                </div>
            </div>
        )}

        {/* Logout confirm */}
        {showLogoutConfirm && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4 animate-fade-in" onClick={() => setShowLogoutConfirm(false)}>
                <div style={{ animation: 'scaleIn 0.25s cubic-bezier(0.16,1,0.3,1) forwards' }} className="bg-base-100 dark:bg-dark-200 p-6 rounded-2xl shadow-2xl border border-base-300 dark:border-dark-300 max-w-sm w-full" onClick={e => e.stopPropagation()}>
                    <div className="flex flex-col items-center text-center mb-6">
                        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center text-red-500 mb-4">
                            <LogOut size={24} />
                        </div>
                        <h3 className="text-xl font-bold text-base-content dark:text-dark-content mb-2">Sign Out?</h3>
                        <p className="text-base-content/60 dark:text-dark-content/60 text-sm">Are you sure you want to sign out? This will clear your local session history.</p>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={() => setShowLogoutConfirm(false)} className="flex-1 px-4 py-2.5 rounded-xl border border-base-300 dark:border-dark-300 font-semibold text-base-content dark:text-dark-content hover:bg-base-200 dark:hover:bg-dark-300 transition-colors">Cancel</button>
                        <button onClick={confirmLogout} className="flex-1 px-4 py-2.5 rounded-xl bg-red-500 text-white font-bold hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20">Sign Out</button>
                    </div>
                </div>
            </div>
        )}
    </div>
  );
};

export default DashboardPage;
